import { useEffect } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from '@/components/TailwindComponents';

interface ExportPDFFormData {
  recipient: string;
  reason: string;
}

const defaultValues: ExportPDFFormData = {
  recipient: '',
  reason: '',
};

interface ExportAsPDFModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (exportData: { recipient: string; reason: string }) => void;
  caseData: {
    caseId: string;
    caseType: string;
    status: string;
    createdBy: string;
  } | null;
}

export const ExportAsPDFModal = ({
  open,
  onClose,
  onConfirm,
}: // caseData,
ExportAsPDFModalProps) => {
  const methods = useForm<ExportPDFFormData>({
    defaultValues,
    mode: 'onSubmit',
  });

  const { handleSubmit, reset } = methods;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = handleSubmit((data: ExportPDFFormData) => {
    onConfirm({
      recipient: data.recipient,
      reason: data.reason,
    });
    // console.log(caseData);
    handleClose();
  });

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[592px] [&>button.absolute]:text-red-500">
        <DialogHeader className="border-b pb-3">
          <DialogTitle>Export as PDF</DialogTitle>
        </DialogHeader>

        <div className="relative">
          <FormProvider {...methods}>
            <form onSubmit={handleFormSubmit} className="space-y-3">
              {/* Recipient field */}
              <div className="space-y-1">
                <Label htmlFor="recipient">
                  Recipient <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="recipient"
                  control={methods.control}
                  rules={{ required: 'Recipient is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="recipient"
                      type="text"
                      placeholder="/"
                      className={
                        methods.formState.errors.recipient
                          ? 'border-red-500'
                          : ''
                      }
                    />
                  )}
                />
                {methods.formState.errors.recipient && (
                  <p className="text-sm text-red-500">
                    {methods.formState.errors.recipient.message}
                  </p>
                )}
              </div>

              {/* Reason field */}
              <div className="space-y-2">
                <Label htmlFor="reason">
                  Reason <span className="text-red-500">*</span>
                </Label>
                <Controller
                  name="reason"
                  control={methods.control}
                  rules={{ required: 'Reason is required' }}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      id="reason"
                      placeholder="/"
                      rows={4}
                      className={
                        methods.formState.errors.reason ? 'border-red-500' : ''
                      }
                    />
                  )}
                />
                {methods.formState.errors.reason && (
                  <p className="text-sm text-red-500">
                    {methods.formState.errors.reason.message}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  className="rounded-md bg-white"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="default" className="rounded-md">
                  Export as PDF
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportAsPDFModal;
